import {
  Card,
  DangerTag,
  DefaultTag,
  Divider,
  Icon,
  SuccessTag,
  Text,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { useCanViewClientPortalPricing } from "../../../../components/permissions/Permissions";
import { LEGACY_INVOICE_STATUS } from "../../../../constants";
import { HistoricalInvoicesActionsDropdownMenu } from "./HistoricalInvoicesActionsDropdownMenu";

interface Props {
  number: string;
  profileName: string;
  total: string | null;
  issuedAt: string;
  status: string;
  id: string;
}

export function HistoricalTableDataCard(props: Props) {
  const { number, profileName, total, issuedAt, status, id } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();

  const getInvoiceLabel = (status: string) => {
    switch (status) {
      case LEGACY_INVOICE_STATUS.draft:
        return (
          <DefaultTag key={LEGACY_INVOICE_STATUS.draft}>
            {t("Draft")}
          </DefaultTag>
        );
      case LEGACY_INVOICE_STATUS.open:
        return (
          <DefaultTag key={LEGACY_INVOICE_STATUS.open}>{t("Open")}</DefaultTag>
        );
      case LEGACY_INVOICE_STATUS.paid:
        return (
          <SuccessTag key={LEGACY_INVOICE_STATUS.paid}>{t("Paid")}</SuccessTag>
        );
      case LEGACY_INVOICE_STATUS.voided:
        return (
          <DangerTag key={LEGACY_INVOICE_STATUS.voided}>
            {t("Voided")}
          </DangerTag>
        );
      case LEGACY_INVOICE_STATUS.pending:
        return (
          <DangerTag key={LEGACY_INVOICE_STATUS.pending}>
            {t("Pending")}
          </DangerTag>
        );
      default:
        return "-";
    }
  };

  return (
    <Card className={styles.card}>
      <div className={styles.headerContainer}>
        <div className={styles.invoiceNumberCell}>
          <div className={styles.invoiceIcon}>
            <Icon name="description" size="space-6" />
          </div>
          <div className={styles.invoiceNumberCellColumns}>
            <Text style="global/heading-medium">{number}</Text>
          </div>
        </div>
        <HistoricalInvoicesActionsDropdownMenu invoiceId={id} />
      </div>
      <div
        className={styles.invoiceDetailsContainer}
        data-testid="invoiceDetailsContainer"
      >
        <div className={styles.profileDetails}>
          <div className={styles.topHalf}>
            <div className={styles.leftIndividual}>
              <Text style="global/heading-medium">{t("Billing profile")}</Text>
              <Text color="text-weak">{profileName}</Text>
            </div>

            <div className={styles.rightIndividual}>
              <Text style="global/heading-medium">{t("Issued at")}</Text>
              <Text color="text-weak">{issuedAt}</Text>
            </div>
          </div>
          <Divider hideBorder marginTop="space-4" />
          <div className={styles.bottomHalf}>
            {canViewClientPortalPricing && (
              <div className={styles.leftIndividual}>
                <Text style="global/heading-medium">{t("Total")}</Text>
                <Text color="text-weak">{total}</Text>
              </div>
            )}

            <div className={styles.rightIndividual}>
              <Text style="global/heading-medium">{t("Status")}</Text>
              <Text color="text-weak">{getInvoiceLabel(status)}</Text>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

const styles = {
  card: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
  }),
  headerContainer: css({
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
  }),
  container: css({
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
  }),
  invoiceDetailsContainer: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    justifyContent: "space-between",
  }),
  profileDetailsContainer: css({
    display: "flex",
    alignItems: "center",
    gap: 10,
  }),
  profileDetails: css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }),
  tags: css({
    display: "flex",
    gap: "var(--space-2)",
    flexWrap: "wrap",
  }),
  invoiceNumberCell: css({
    ":hover": {
      cursor: "pointer",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "100%",
    marginBottom: "var(--space-4)",
  }),
  invoiceNumberCellColumns: css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }),
  invoiceIcon: css({
    marginRight: "10px",
    flexDirection: "column",
  }),
  topHalf: css({
    display: "flex",
    flexDirection: "row",
  }),
  bottomHalf: css({
    display: "flex",
    flexDirection: "row",
  }),
  leftIndividual: css({
    display: "flex",
    flexDirection: "column",
    marginRight: "var(--space-10)",
    width: "50%",
  }),
  rightIndividual: css({
    display: "flex",
    flexDirection: "column",
  }),
};
